import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { 
  Search, Brain, Download, Calendar, User, CheckCircle, 
  XCircle, Clock, TrendingUp, BarChart3
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface PsychometricRecord {
  id: string;
  display_name: string;
  full_name: string;
  team: string;
  position: string;
  site_id: string | null;
  site_name?: string;
  psychometric_profile: Record<string, any> | null;
  baseline_risk_score: number | null;
  created_at: string;
  updated_at: string;
}

interface PsychometricStats {
  totalWorkers: number;
  assessedCount: number;
  pendingCount: number;
  avgBaselineRisk: number;
  recentAssessments: number;
}

interface AdminPsychometricsProps {
  siteId?: string | null;
}

const AdminPsychometrics = ({ siteId: propSiteId }: AdminPsychometricsProps = {}) => {
  const { toast } = useToast();
  const [records, setRecords] = useState<PsychometricRecord[]>([]);
  const [stats, setStats] = useState<PsychometricStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState(propSiteId || 'all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'assessed' | 'pending'>('all');

  useEffect(() => {
    fetchPsychometricData();
  }, [selectedSite]);

  const fetchPsychometricData = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('worker_profiles')
        .select(`
          id, display_name, full_name, team, position, site_id,
          psychometric_profile, baseline_risk_score,
          created_at, updated_at,
          site:company_locations!site_id(location_name)
        `)
        .order('updated_at', { ascending: false });

      if (selectedSite !== 'all') {
        query = query.eq('site_id', selectedSite);
      }

      const { data, error } = await query;
      if (error) throw error;

      const transformedRecords: PsychometricRecord[] = (data || []).map((r: any) => ({
        id: r.id,
        display_name: r.display_name,
        full_name: r.full_name || '',
        team: r.team,
        position: r.position || '',
        site_id: r.site_id,
        site_name: r.site?.location_name,
        psychometric_profile: r.psychometric_profile,
        baseline_risk_score: r.baseline_risk_score,
        baseline_calculated_at: r.baseline_calculated_at,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));

      setRecords(transformedRecords);

      // Calculate stats
      const total = transformedRecords.length;
      const assessed = transformedRecords.filter(r => 
        r.psychometric_profile && Object.keys(r.psychometric_profile).length > 0
      ).length;
      const pending = total - assessed;
      
      const withBaseline = transformedRecords.filter(r => r.baseline_risk_score !== null);
      const avgBaseline = withBaseline.length > 0 
        ? withBaseline.reduce((sum, r) => sum + (r.baseline_risk_score || 0), 0) / withBaseline.length 
        : 0;

      // Recent assessments (last 7 days) - use updated_at since baseline_calculated_at doesn't exist
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const recent = transformedRecords.filter(r => 
        r.psychometric_profile && r.updated_at && new Date(r.updated_at) > weekAgo
      ).length;

      setStats({
        totalWorkers: total,
        assessedCount: assessed,
        pendingCount: pending,
        avgBaselineRisk: Math.round(avgBaseline),
        recentAssessments: recent,
      });

    } catch (error) {
      console.error('Error fetching psychometric data:', error);
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Name', 'Team', 'Position', 'Site', 
      'Negative Urgency', 'Lack of Premeditation', 'Lack of Perseverance', 
      'Sensation Seeking', 'Positive Urgency', 'DOSPERT H/S', 'Delay Discounting',
      'Baseline Risk Score', 'Assessment Date'
    ];

    const rows = records
      .filter(r => r.psychometric_profile && Object.keys(r.psychometric_profile).length > 0)
      .map(r => {
        const upps = r.psychometric_profile?.upps_p || {};
        return [
          r.display_name,
          r.team,
          r.position,
          r.site_name || '',
          upps.negative_urgency || '',
          upps.lack_of_premeditation || '',
          upps.lack_of_perseverance || '',
          upps.sensation_seeking || '',
          upps.positive_urgency || '',
          r.psychometric_profile?.dospert_health_safety || '',
          r.psychometric_profile?.delay_discounting_k || '',
          r.baseline_risk_score || '',
          r.updated_at ? format(new Date(r.updated_at), 'yyyy-MM-dd') : ''
        ].join(',');
      });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `psychometric-data-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Export Complete', description: `Exported ${rows.length} records` });
  };

  const hasProfile = (r: PsychometricRecord) => 
    r.psychometric_profile && Object.keys(r.psychometric_profile).length > 0;

  const filteredRecords = records.filter(r => {
    const matchesSearch = 
      r.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.team.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'assessed' && hasProfile(r)) ||
      (filterStatus === 'pending' && !hasProfile(r));

    return matchesSearch && matchesFilter;
  });

  const formatScore = (score: number | undefined) => {
    if (score === undefined || score === null) return null;
    return Math.round(score * 100);
  };

  // Calculate baseline risk score on-the-fly if not set
  const calculateDisplayBaseline = (record: PsychometricRecord): number | null => {
    // If baseline already calculated, use it
    if (record.baseline_risk_score && record.baseline_risk_score > 0) {
      return record.baseline_risk_score;
    }
    
    // Calculate on-the-fly from psychometric profile
    const profile = record.psychometric_profile;
    if (!profile || !profile.upps_p) return null;
    
    const upps = profile.upps_p;
    const lackOfPerseverance = upps.lack_of_perseverance || 0;
    const negativeUrgency = upps.negative_urgency || 0;
    const positiveUrgency = upps.positive_urgency || 0;
    const lackOfPremeditation = upps.lack_of_premeditation || 0;
    const sensationSeeking = upps.sensation_seeking || 0;
    const dospertHealthSafety = profile.dospert_health_safety || 0;
    const delayDiscounting = profile.delay_discounting_k || 0;
    
    // Formula: L×0.40 + N×0.25 + Other_UPPS×0.10 + DD×0.10 + DOSPERT×0.15
    const otherUpps = (positiveUrgency + lackOfPremeditation + sensationSeeking) / 3;
    const baseline = Math.round(
      (lackOfPerseverance * 0.40 +
       negativeUrgency * 0.25 +
       otherUpps * 0.10 +
       delayDiscounting * 0.10 +
       dospertHealthSafety * 0.15) * 100
    );
    
    return baseline;
  };

  // Get cell background color based on score threshold
  const getCellColor = (score: number | undefined) => {
    if (score === undefined || score === null) return '';
    const pct = score * 100;
    if (pct >= 70) return 'bg-destructive/20 text-destructive';
    if (pct >= 50) return 'bg-warning/20 text-warning';
    if (pct >= 30) return 'bg-yellow-500/20 text-yellow-600';
    return 'bg-success/20 text-success';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            Psychometric Assessments
          </h2>
          <p className="text-muted-foreground">
            Raw psychometric data from worker onboarding assessments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SiteFilter value={selectedSite} onChange={setSelectedSite} />
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Workers</p>
                <p className="text-3xl font-bold">{stats?.totalWorkers}</p>
              </div>
              <User className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-success/10 to-success/5 border-success/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Assessed</p>
                <p className="text-3xl font-bold text-success">{stats?.assessedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-success/50" />
            </div>
            <Progress 
              value={stats?.totalWorkers ? (stats.assessedCount / stats.totalWorkers) * 100 : 0} 
              className="h-1.5 mt-2" 
            />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold text-warning">{stats?.pendingCount}</p>
              </div>
              <XCircle className="h-8 w-8 text-warning/50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Avg Baseline Risk</p>
                <p className={cn(
                  "text-3xl font-bold",
                  (stats?.avgBaselineRisk || 0) >= 60 ? "text-destructive" :
                  (stats?.avgBaselineRisk || 0) >= 30 ? "text-warning" : "text-success"
                )}>{stats?.avgBaselineRisk}%</p>
              </div>
              <BarChart3 className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">This Week</p>
                <p className="text-3xl font-bold">{stats?.recentAssessments}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">new assessments</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'assessed', 'pending'] as const).map((filter) => (
                <Button
                  key={filter}
                  variant={filterStatus === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(filter)}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <span className="text-sm font-medium">Risk Level:</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-destructive/20 border border-destructive/30" />
            <span className="text-xs">High (≥70%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-warning/20 border border-warning/30" />
            <span className="text-xs">Medium (50-69%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-yellow-500/20 border border-yellow-500/30" />
            <span className="text-xs">Low-Med (30-49%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-success/20 border border-success/30" />
            <span className="text-xs">Low (&lt;30%)</span>
          </div>
        </div>
      </Card>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Psychometric Heatmap</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-3 font-medium sticky left-0 bg-muted/30">Worker</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">Neg. Urg.</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">Lack Prem.</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">Lack Pers.</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">Sensation</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">Pos. Urg.</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">DOSPERT</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[80px]">Delay Disc.</th>
                  <th className="text-center py-3 px-2 font-medium min-w-[90px]">Baseline</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => {
                  const upps = record.psychometric_profile?.upps_p || {};
                  const assessed = hasProfile(record);
                  
                  if (!assessed) return null; // Only show assessed workers in heatmap
                  
                  return (
                    <tr key={record.id} className="border-b hover:bg-muted/20">
                      <td className="py-2 px-3 sticky left-0 bg-card">
                        <div>
                          <p className="font-semibold">{record.display_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {record.team} • {record.site_name || 'No site'}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(upps.negative_urgency)
                        )}>
                          {formatScore(upps.negative_urgency) !== null ? `${formatScore(upps.negative_urgency)}%` : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(upps.lack_of_premeditation)
                        )}>
                          {formatScore(upps.lack_of_premeditation) !== null ? `${formatScore(upps.lack_of_premeditation)}%` : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(upps.lack_of_perseverance)
                        )}>
                          {formatScore(upps.lack_of_perseverance) !== null ? `${formatScore(upps.lack_of_perseverance)}%` : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(upps.sensation_seeking)
                        )}>
                          {formatScore(upps.sensation_seeking) !== null ? `${formatScore(upps.sensation_seeking)}%` : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(upps.positive_urgency)
                        )}>
                          {formatScore(upps.positive_urgency) !== null ? `${formatScore(upps.positive_urgency)}%` : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(record.psychometric_profile?.dospert_health_safety)
                        )}>
                          {formatScore(record.psychometric_profile?.dospert_health_safety) !== null 
                            ? `${formatScore(record.psychometric_profile?.dospert_health_safety)}%` 
                            : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        <div className={cn(
                          "rounded-md px-2 py-1 font-mono font-bold text-sm",
                          getCellColor(record.psychometric_profile?.delay_discounting_k)
                        )}>
                          {formatScore(record.psychometric_profile?.delay_discounting_k) !== null 
                            ? `${formatScore(record.psychometric_profile?.delay_discounting_k)}%` 
                            : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-1 text-center">
                        {(() => {
                          const baseline = calculateDisplayBaseline(record);
                          if (baseline !== null) {
                            return (
                              <div className={cn(
                                "rounded-md px-2 py-1 font-mono font-bold text-sm",
                                baseline >= 60 ? "bg-destructive/20 text-destructive" :
                                baseline >= 30 ? "bg-warning/20 text-warning" : "bg-success/20 text-success"
                              )}>
                                {baseline}%
                              </div>
                            );
                          }
                          return <span className="text-muted-foreground">-</span>;
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredRecords.filter(r => hasProfile(r)).length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No records match your filters
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPsychometrics;
